import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Admin Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import CalendarPage from "./pages/CalendarPage";
import ReservationsPage from "./pages/ReservationsPage";
import ExpensesPage from "./pages/ExpensesPage";
import PropertiesPage from "./pages/PropertiesPage";
import PlatformSyncPage from "./pages/PlatformSyncPage";
import SettingsPage from "./pages/SettingsPage";
import BookingsManagementPage from "./pages/admin/BookingsManagementPage";
import PropertyEditorPage from "./pages/admin/PropertyEditorPage";
import SiteConfigPage from "./pages/admin/SiteConfigPage";

// Public Pages (Premium Era Lodge)
import LandingPage from "./pages/public/LandingPage";
import ReservationPage from "./pages/public/ReservationPage";
import ConfirmationPage from "./pages/public/ConfirmationPage";

// Layout
import DashboardLayout from "./components/layout/DashboardLayout";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0C0B] flex items-center justify-center">
        <div className="font-display text-2xl text-[#D4AF37] animate-pulse">Era Lodge</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

const AdminPublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0C0B] flex items-center justify-center">
        <div className="font-display text-2xl text-[#D4AF37] animate-pulse">Era Lodge</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/admin/calendrier" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* PUBLIC SITE (Era Lodge premium) */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/reserver/:propertyId" element={<ReservationPage />} />
      <Route path="/confirmation/:bookingId" element={<ConfirmationPage />} />

      {/* ADMIN AUTH */}
      <Route path="/admin/login" element={<AdminPublicRoute><LoginPage /></AdminPublicRoute>} />
      <Route path="/admin/register" element={<AdminPublicRoute><RegisterPage /></AdminPublicRoute>} />
      <Route path="/admin/forgot-password" element={<AdminPublicRoute><ForgotPasswordPage /></AdminPublicRoute>} />

      {/* ADMIN PROTECTED */}
      <Route path="/admin" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin/calendrier" replace />} />
        <Route path="calendrier" element={<CalendarPage />} />
        <Route path="reservations" element={<ReservationsPage />} />
        <Route path="reservations-voyageurs" element={<BookingsManagementPage />} />
        <Route path="depenses" element={<ExpensesPage />} />
        <Route path="proprietes" element={<PropertiesPage />} />
        <Route path="proprietes/:propertyId/edit" element={<PropertyEditorPage />} />
        <Route path="site-config" element={<SiteConfigPage />} />
        <Route path="synchronisation" element={<PlatformSyncPage />} />
        <Route path="parametres" element={<SettingsPage />} />
        <Route path="tableau-de-bord" element={<DashboardPage />} />
      </Route>

      {/* LEGACY ADMIN ROUTES (redirect to /admin) */}
      <Route path="/login" element={<Navigate to="/admin/login" replace />} />
      <Route path="/register" element={<Navigate to="/admin/register" replace />} />
      <Route path="/forgot-password" element={<Navigate to="/admin/forgot-password" replace />} />
      <Route path="/calendrier" element={<Navigate to="/admin/calendrier" replace />} />
      <Route path="/reservations" element={<Navigate to="/admin/reservations" replace />} />
      <Route path="/reservations-voyageurs" element={<Navigate to="/admin/reservations-voyageurs" replace />} />
      <Route path="/depenses" element={<Navigate to="/admin/depenses" replace />} />
      <Route path="/proprietes" element={<Navigate to="/admin/proprietes" replace />} />
      <Route path="/synchronisation" element={<Navigate to="/admin/synchronisation" replace />} />
      <Route path="/parametres" element={<Navigate to="/admin/parametres" replace />} />
      <Route path="/tableau-de-bord" element={<Navigate to="/admin/tableau-de-bord" replace />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton theme="dark" />
        <div id="portal-root" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
